import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Horse Racing Schedule (desktop home). Mirrors the exchange reference UI:
// a "Horse Racing Schedule" panel with country flag tabs (ALL, AUS, FRA, RSA,
// GB, IRE) and a list of upcoming races (venue + date/time + multi-market bell).
//
// Data comes from the same sportsData feed used elsewhere on Home. For horse
// racing, `eventName` holds the venue (e.g. "Eagle Farm") and `seriesName`
// holds the country code (e.g. "AUS"). We only need those plus the datetime
// and ids to add the race to multi-market.

// Country tabs shown in the reference, in order. flag = emoji used as the tab icon.
const COUNTRY_TABS = [
  { code: "ALL", label: "ALL", flag: "" },
  { code: "AUS", label: "AUS", flag: "🇦🇺" },
  { code: "FRA", label: "FRA", flag: "🇫🇷" },
  { code: "RSA", label: "RSA", flag: "🇿🇦" },
  { code: "GB", label: "GB", flag: "🇬🇧" },
  { code: "IRE", label: "IRE", flag: "🇮🇪" },
];

// Format the race open time as "DD-MM-YYYY HH:mm" matching the reference. The
// reference renders times in the Indian locale (GMT+5:30), so we format using
// the Asia/Kolkata timezone.
const formatRaceTime = (iso) => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")}-${get("month")}-${get("year")} ${get("hour")}:${get("minute")}`;
};

// Short "HH:mm" time used for the per-venue time chips (country tabs).
const formatChipTime = (iso) => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const HorseRacingSchedule = ({ races = [], onAddMultiMarket }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("ALL");

  // Keep only the country tabs that actually have races, but always keep ALL.
  const availableTabs = useMemo(() => {
    const present = new Set(races.map((r) => (r.seriesName || "").toUpperCase()));
    return COUNTRY_TABS.filter((t) => t.code === "ALL" || present.has(t.code));
  }, [races]);

  const filteredRaces = useMemo(() => {
    const list = activeTab === "ALL"
      ? races
      : races.filter((r) => (r.seriesName || "").toUpperCase() === activeTab);
    return [...list].sort(
      (a, b) => new Date(a.eventDateTime).getTime() - new Date(b.eventDateTime).getTime()
    );
  }, [races, activeTab]);

  // When a country tab is active, group its races by venue so each venue shows
  // a row of clickable time chips (matching the reference).
  const venueGroups = useMemo(() => {
    if (activeTab === "ALL") return [];
    const map = new Map();
    filteredRaces.forEach((race) => {
      const venue = (race.eventName || "").trim();
      if (!map.has(venue)) map.set(venue, []);
      map.get(venue).push(race);
    });
    return Array.from(map.entries()).map(([venue, items]) => ({ venue, items }));
  }, [filteredRaces, activeTab]);

  if (!races.length) return null;

  const openRace = (race) => {
    if (race?.eventId && race?.marketId) {
      navigate(`/match-details/${race.eventId}/${race.marketId}`, { state: race });
    }
  };

  const handlePin = (e, race) => {
    e.stopPropagation();
    if (onAddMultiMarket && race?.eventId) onAddMultiMarket(race.eventId);
  };

  return (
    <section className="hr-schedule" aria-label="Horse Racing Schedule">
      <style>{`
        @keyframes inplayColorLoop {
          0%   { color: #d32f2f; }
          50%  { color: #508d0e; }
          100% { color: #d32f2f; }
        }
        .hr-schedule .inplay-blink {
          animation: inplayColorLoop 0.8s ease-in-out infinite;
        }
      `}</style>
      <h2 className="hr-schedule__title">Horse Racing Schedule</h2>

      <div className="hr-schedule__tabs" role="tablist" aria-label="Horse racing countries">
        {availableTabs.map((tab) => (
          <button
            type="button"
            role="tab"
            key={tab.code}
            aria-selected={activeTab === tab.code}
            className={`hr-schedule__tab ${activeTab === tab.code ? "is-active" : ""}`}
            onClick={() => setActiveTab(tab.code)}
          >
            {tab.flag && <span className="hr-schedule__flag" aria-hidden="true">{tab.flag}</span>}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === "ALL" ? (
        <div className="hr-schedule__list">
          {filteredRaces.length === 0 ? (
            <div className="hr-schedule__empty">No races available</div>
          ) : (
            filteredRaces.map((race) => (
              <div
                key={race.eventId || race._id}
                className="hr-schedule__row"
                onClick={() => openRace(race)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openRace(race);
                }}
              >
                <span className="hr-schedule__venue">{race.eventName}</span>
                {race.status === "in_play" ? (
                  <span className="hr-schedule__live inplay-blink">In-Play</span>
                ) : (
                  <span className="hr-schedule__time">{formatRaceTime(race.eventDateTime)}</span>
                )}
                <button
                  type="button"
                  className="hr-schedule__pin"
                  aria-label="Add to multi market"
                  title="Add to multi market"
                  onClick={(e) => handlePin(e, race)}
                >
                  <svg width="25" height="25" viewBox="0 0 25 25" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill={race.multi_market > 0 ? "rgb(107,189,17)" : "rgb(126,151,167)"}
                      d="M12.5 25C5.596 25 0 19.404 0 12.5S5.596 0 12.5 0 25 5.596 25 12.5 19.404 25 12.5 25zm0-1C18.85 24 24 18.85 24 12.5S18.85 1 12.5 1 1 6.15 1 12.5 6.15 24 12.5 24zm5.09-12.078c1.606.516 2.41 1.13 2.41 2.19 0 .373-.067.616-.2.73-.135.115-.403.173-.804.173H13.57l-.81 7.988h-.536l-.795-7.988H6.003c-.4 0-.67-.065-.803-.194-.133-.128-.2-.364-.2-.708 0-1.06.804-1.674 2.41-2.19.09 0 .18-.03.27-.086.49-.172.802-.444.936-.816L9.82 5.95v-.216c0-.23-.222-.415-.668-.558l-.067-.043h-.067c-.536-.143-.804-.387-.804-.73 0-.402.09-.652.268-.753.18-.1.49-.15.938-.15h6.16c.447 0 .76.05.938.15.178.1.268.35.268.752 0 .344-.268.588-.804.73h-.067l-.067.044c-.446.143-.67.33-.67.558v.215l1.206 5.07c.134.372.446.644.937.816.09.057.18.086.27.086z"
                    />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="hr-schedule__venues">
          {venueGroups.length === 0 ? (
            <div className="hr-schedule__empty">No races available</div>
          ) : (
            venueGroups.map((group) => (
              <div key={group.venue} className="hr-schedule__venue-row">
                <span className="hr-schedule__venue">{group.venue}</span>
                <div className="hr-schedule__chips">
                  {group.items.map((race) => (
                    <button
                      type="button"
                      key={race.eventId || race._id}
                      className={`hr-schedule__chip ${race.status === "in_play" ? "is-live" : ""}`}
                      onClick={() => openRace(race)}
                      title={`Open ${group.venue}`}
                    >
                      {formatChipTime(race.eventDateTime)}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
};

export default HorseRacingSchedule;
